// Routes + the login gate. A 401 anywhere sends the person to /login.

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { UNAUTHORIZED_EVENT } from './api';
import { useMe } from './hooks/useMe';
import { Shell } from './components/Shell';
import { Loading, ErrorBox } from './components/States';
import { LoginPage } from './pages/Login';
import { InboxPage } from './pages/inbox/InboxPage';
import { ContactsPage } from './pages/contacts/ContactsPage';
import { ContactPage } from './pages/contacts/ContactPage';
import { ScheduledPage } from './pages/scheduled/ScheduledPage';
import { TemplatesPage } from './pages/scheduled/TemplatesPage';
import { HealthPage } from './pages/health/HealthPage';
import { SelfTestPage } from './pages/health/SelfTestPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { ListsPage } from './pages/lists/ListsPage';
import { ImportDetailPage } from './pages/lists/ImportDetailPage';

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  useEffect(() => {
    const onUnauthorized = () => {
      qc.removeQueries({ queryKey: ['me'] });
      if (location.pathname !== '/login') navigate('/login', { replace: true, state: { from: location.pathname } });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [navigate, location.pathname, qc]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<Authed />} />
    </Routes>
  );
}

function Authed() {
  const me = useMe();
  const location = useLocation();

  if (me.isPending) return <Loading label="Signing you in" />;
  if (me.isError) {
    if (me.error.status === 401) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    return (
      <div className="page narrow">
        <ErrorBox error={me.error} retry={() => me.refetch()} />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<Shell me={me.data} />}>
        <Route index element={<Navigate to="/inbox" replace />} />
        <Route path="inbox" element={<InboxPage me={me.data} />} />
        <Route path="inbox/:conversationId" element={<InboxPage me={me.data} />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:contactId" element={<ContactPage me={me.data} />} />
        <Route path="scheduled" element={<ScheduledPage me={me.data} />} />
        <Route path="scheduled/templates" element={<TemplatesPage />} />
        <Route path="lists" element={<ListsPage me={me.data} />} />
        <Route path="lists/:importId" element={<ImportDetailPage me={me.data} />} />
        <Route path="health" element={<HealthPage me={me.data} />} />
        <Route path="health/self-test" element={<SelfTestPage me={me.data} />} />
        <Route path="settings/*" element={<SettingsPage me={me.data} />} />
        <Route path="*" element={<Navigate to="/inbox" replace />} />
      </Route>
    </Routes>
  );
}
